import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../flux/stores/hooks';
import { fetchJobs, fetchJob, triggerJob } from '../../../../flux/actions/jobsThunks';
import { clearTriggerError } from '../../../../flux/stores/jobsReducer';
import { isEvaluationAlreadyActive, activeEvaluationJobId } from '../../../../flux/facades/jobsApi';
import { useApiAccessToken } from '../../../../utils/useApiAccessToken';
import { useJobsPolling } from '../../../../utils/useJobsPolling';
import { formatDateTime } from '../../../../utils/formatDate';
import { JOB_STATUS_PRESENTATION, TRIGGER_LABELS } from '../../../../utils/jobPresentation';
import { StatusBadge } from '../../../common/StatusBadge/StatusBadge';
import { Spinner } from '../../../common/Spinner/Spinner';

const EMPTY_JOBS = { status: 'idle', data: [], error: null };
const EMPTY_TRIGGER = { isSaving: false, error: null };
const ACTIVE_STATUSES = ['queued', 'running'];
const POLL_INTERVAL_MS = 5000;

export const ProjectChecks = ({ projectId }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { getAccessToken } = useApiAccessToken();
    const jobs = useAppSelector(state => state.jobs.byProject[projectId]?.jobs) ?? EMPTY_JOBS;
    const trigger = useAppSelector(state => state.jobs.byProject[projectId]?.trigger) ?? EMPTY_TRIGGER;

    const withToken = useCallback(async (run) => {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) return null;
            return await run(accessToken);
        } catch (error) {
            console.error('Failed to acquire API token for checks:', error);
            return null;
        }
    }, [getAccessToken]);

    const loadJobs = useCallback(
        () => withToken(accessToken => dispatch(fetchJobs({ accessToken, projectId }))),
        [withToken, dispatch, projectId]
    );

    useEffect(() => {
        if (jobs.status !== 'idle') return;
        loadJobs();
    }, [jobs.status, loadJobs]);

    const activeJob = jobs.data.find(job => ACTIVE_STATUSES.includes(job.status));
    const hasActiveJob = Boolean(activeJob);

    const pollActiveJob = useCallback(() => {
        if (!activeJob) return null;
        return withToken(accessToken => dispatch(fetchJob({ accessToken, projectId, jobId: activeJob.object_id })));
    }, [withToken, dispatch, projectId, activeJob]);
    useJobsPolling(hasActiveJob, pollActiveJob, POLL_INTERVAL_MS);

    const handleTrigger = async () => {
        dispatch(clearTriggerError(projectId));
        const result = await withToken(accessToken => dispatch(triggerJob({ accessToken, projectId })));
        if (result && triggerJob.rejected.match(result) && isEvaluationAlreadyActive(result.payload ?? {})) {
            const existingJobId = activeEvaluationJobId(result.payload);
            if (existingJobId) withToken(accessToken => dispatch(fetchJob({ accessToken, projectId, jobId: existingJobId })));
            else loadJobs();
        }
    };

    const isLoading = jobs.status === 'idle' || jobs.status === 'loading';
    const activeStatusLabel = activeJob ? JOB_STATUS_PRESENTATION[activeJob.status]?.label ?? 'Running' : null;

    return (
        <div>
            <div className="rules-header">
                <div>
                    <h2 className="pw-page-title">Checks</h2>
                    <p className="rules-page-subtitle">Run this project's active rules against its model groups.</p>
                </div>
                <button
                    type="button"
                    className="rules-btn-primary"
                    onClick={handleTrigger}
                    disabled={trigger.isSaving || hasActiveJob}
                >
                    {trigger.isSaving ? (
                        <><Spinner size={14} style={{ marginRight: 6 }} /> Starting...</>
                    ) : hasActiveJob ? (
                        <><Spinner size={14} style={{ marginRight: 6 }} /> {activeStatusLabel}...</>
                    ) : (
                        'Run check'
                    )}
                </button>
            </div>

            {trigger.error && (
                <div className="rules-alert rules-alert-error" role="alert">
                    <span>
                        {isEvaluationAlreadyActive(trigger.error)
                            ? 'A check is already running for this project.'
                            : trigger.error.message}
                    </span>
                </div>
            )}

            {jobs.error && (
                <div className="rules-alert rules-alert-error" role="alert">
                    <span>{jobs.error.message}</span>
                    <button type="button" className="rules-btn-ghost" onClick={loadJobs}>Retry</button>
                </div>
            )}

            {isLoading ? (
                <p className="projects-loading">Loading checks...</p>
            ) : jobs.data.length === 0 ? (
                <div className="rules-empty"><p>No checks run yet.</p></div>
            ) : (
                <div className="rules-table-wrapper">
                    <table className="rules-table">
                        <thead>
                            <tr>
                                <th className="rules-th">Status</th>
                                <th className="rules-th">Trigger</th>
                                <th className="rules-th">Queued</th>
                                <th className="rules-th">Started</th>
                                <th className="rules-th">Finished</th>
                            </tr>
                        </thead>
                        <tbody>
                            {jobs.data.map((job) => {
                                const presentation = JOB_STATUS_PRESENTATION[job.status];
                                return (
                                    <tr
                                        key={job.object_id}
                                        className="rules-tr projects-tr-clickable"
                                        onClick={() => navigate(`/projects/${projectId}/checks/${job.object_id}`)}
                                    >
                                        <td className="rules-td">
                                            <StatusBadge label={presentation.label} variant={presentation.variant} />
                                        </td>
                                        <td className="rules-td">{TRIGGER_LABELS[job.trigger_type] ?? job.trigger_type}</td>
                                        <td className="rules-td">{formatDateTime(job.queued_at)}</td>
                                        <td className="rules-td">{formatDateTime(job.started_at)}</td>
                                        <td className="rules-td">{formatDateTime(job.finished_at)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ProjectChecks;
